import { useState } from 'react';
import { useWizardStore } from '../store/wizardStore';
import { StepShell } from '../components/StepShell';
import { SignaturePad } from '../components/SignaturePad';
import { Button } from '../components/ui/Button';

type SigIndex = 0 | 1 | 2;
type Phase = 'employee' | 'client';

export function Step4_Signatures() {
  const variant = useWizardStore((s) => s.variant);
  const signingPersonId = useWizardStore((s) => s.signingPersonId);
  const signatures = useWizardStore((s) => s.signatures);
  const setSig = useWizardStore((s) => s.setSignature);
  const setEmployeeSignature = useWizardStore((s) => s.setEmployeeSignature);
  const goTo = useWizardStore((s) => s.goTo);

  const isOffice = signingPersonId === 'office';
  const employeeComplete = Boolean(signatures.employee);

  const [phase, setPhase] = useState<Phase>(isOffice || employeeComplete ? 'client' : 'employee');
  const [idx, setIdx] = useState<SigIndex>(0);

  const docs: Array<{
    title: string;
    key: 'windowsKp' | 'gate';
  }> =
    variant === 'variant1'
      ? [{ title: 'Договор купли-продажи окон (ООО «Ворота-Окна»)', key: 'windowsKp' }]
      : variant === 'variant3'
        ? [{ title: 'Договор купли-продажи ворот (ИП Шалеев А.Г.)', key: 'gate' }]
        : [{
            title: 'Договор купли-продажи и договор подряда на монтаж окон',
            key: 'windowsKp',
          }];

  if (phase === 'employee' && !isOffice) {
    const employeeSubtitle =
      variant === 'variant2'
        ? 'Распишитесь от имени организации — подпись попадёт в оба договора (купли-продажи и подряда)'
        : 'Распишитесь от имени организации — подпись попадёт в левую часть договора';

    return (
      <StepShell
        title="Подпись сотрудника"
        subtitle={employeeSubtitle}
        current="signatures"
        footer={
          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => goTo('review')}>
              Назад
            </Button>
            <Button
              disabled={!signatures.employee}
              onClick={() => setPhase('client')}
            >
              Далее — подпись клиента →
            </Button>
          </div>
        }
      >
        <SignaturePad
          key="employee"
          title="Подпись сотрудника (представитель продавца)"
          initialValue={signatures.employee}
          onConfirm={setEmployeeSignature}
        />
      </StepShell>
    );
  }

  const current = docs[idx];
  if (!current) return null;
  const isLast = idx === docs.length - 1;
  const allClientSigned = docs.every((d) => signatures[d.key]);
  const canAdvance = isLast ? allClientSigned && (isOffice || employeeComplete) : !!signatures[current.key];

  const goBackFromClient = () => {
    if (idx === 0) {
      if (isOffice) {
        goTo('review');
      } else {
        setPhase('employee');
      }
    } else {
      setIdx((idx - 1) as SigIndex);
    }
  };

  return (
    <StepShell
      title={docs.length > 1 ? `Подпись клиента ${idx + 1} из ${docs.length}` : 'Подпись клиента'}
      subtitle={current.title}
      current="signatures"
      footer={
        <div className="flex justify-between">
          <Button variant="secondary" onClick={goBackFromClient}>
            Назад
          </Button>
          <Button
            disabled={!canAdvance}
            onClick={() => {
              if (isLast) goTo('insert');
              else setIdx((idx + 1) as SigIndex);
            }}
          >
            {isLast ? 'Далее — Вставка →' : 'Следующая подпись →'}
          </Button>
        </div>
      }
    >
      <SignaturePad
        key={current.key}
        title={current.title}
        initialValue={signatures[current.key]}
        onConfirm={(png) => setSig(current.key, png)}
      />
    </StepShell>
  );
}
